import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { enums } from '../../utils';
import { LoadingCoverSmall, Text } from '../base/tw-components';
import BasicClaimsCard from './basic-claim-card';
import Button from '../base/button';
import { ButtonTypes } from '../../utils/enums';
import { log } from '../../utils/log';
import FadeLoader from 'react-spinners/FadeLoader';
import { colors } from '../../style';

type SessionData = {
  duplicateClaims: Record<string, any>;
  values: Record<string, any>;
  isValid: boolean;
  isLastStep: boolean;
  isPreviousStepsValid: boolean;
};

type LocationState = { key?: string } | undefined;

type FontProps = {
  children: React.ReactNode;
  className?: string;
};

const Title = ({ children, className = '' }: FontProps) => {
  return (
    <h1
      className={`tw-text-2xl md:tw-text-4xl tw-font-normal tw-text-unm-interactive01-800 tw-mb-5 tw-leading-10 ${className}`}
    >
      {children}
    </h1>
  );
};

const INTAKE_TYPE = 'intake-type';

const DuplicateClaims: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();

  const [data, setData] = React.useState<SessionData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const dataLoadedRef = React.useRef(false);

  React.useEffect(() => {
    // Guard against React 18 StrictMode double-invocation.
    // On the first run we read + remove sessionStorage. On the second
    // run the item is gone, but data was already set via setData, so
    // we simply skip to avoid an incorrect redirect.
    if (dataLoadedRef.current) {
      setLoading(false);
      return;
    }

    const key = location.state?.key;

    if (!key) {
      history.replace(enums.AppPaths.startClaim);
      return;
    }

    const raw = sessionStorage.getItem(key);
    if (!raw) {
      history.replace(enums.AppPaths.startClaim);
      return;
    }

    try {
      const parsed: SessionData = JSON.parse(raw);
      const isObject = parsed && typeof parsed === 'object';
      const hasAll =
        isObject &&
        'duplicateClaims' in parsed &&
        'values' in parsed &&
        'isValid' in parsed &&
        'isLastStep' in parsed &&
        'isPreviousStepsValid' in parsed;

      if (!hasAll) throw new Error('Invalid session payload');
      setData(parsed);
      dataLoadedRef.current = true;
      sessionStorage.removeItem(key);
    } catch (err) {
      log.error(err);
      history.replace(enums.AppPaths.startClaim);
      return;
    } finally {
      setLoading(false);
    }
  }, [history, location.state]);

  if (loading)
    return (
      <LoadingCoverSmall id="loading-cover">
        <FadeLoader
          height={14}
          width={5}
          radius={2}
          margin={0}
          color={colors.blue}
        />
      </LoadingCoverSmall>
    );
  if (!data) return null;

  const { duplicateClaims, values, isLastStep } = data;

  const handleStartClaim = () => {
    if (values[INTAKE_TYPE] === 'supplemental') {
      history.push(enums.AppPaths.supplemental);
    } else if (!isLastStep && values[INTAKE_TYPE] !== 'none') {
      history.push(`${enums.AppPaths.form}/${values[INTAKE_TYPE]}`);
    } else {
      history.push(`${enums.AppPaths.form}/${values['leave-type']}`);
    }
  };

  const hasMultiple = duplicateClaims?.length > 1;

  return (
    <div
      id="duplicate-claims-view"
      className="tw-w-full lg:tw-w-9/12 tw-mx-auto tw-px-0"
    >
      <Title>
        {hasMultiple ? 'Similar claims found' : 'Similar claim found'}
      </Title>
      <Text fontSize="lg" color="unm-primary02-500" className="tw-mb-6">
        {hasMultiple
          ? 'We found similar claims on file. Would you like to review them before submitting a new one?'
          : 'We found a similar claim on file. Would you like to review it before submitting a new one?'}
      </Text>
      {duplicateClaims.map((claim: any, index: number) => (
        <div key={`claim-card-${claim?.NTNId}`} className="tw-mb-4">
          <BasicClaimsCard keyId={index} claim={claim} />
        </div>
      ))}
      <div className="tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-end tw-pb-1 tw-mt-8">
        <Button
          className="tw-w-full md:tw-w-auto tw-px-5 tw-py-1"
          onClick={handleStartClaim}
          buttonStyle={ButtonTypes.outline}
          id={'start-a-new-claim'}
          data-gtm-dl={JSON.stringify({
            attribute1: 'file a claim',
            attribute2: 'similar claim found - continue with claim',
            attribute3: 'claim_interaction',
            attribute4: 'undefined',
          })}
        >
          Continue with claim
        </Button>
      </div>
    </div>
  );
};

export default DuplicateClaims;
